import { idSchema } from '@/lib/utils'
import { isSubscriptionPackageCollection } from '@/lib/subscription-package-collection'
import { z } from 'zod/v4'

export const collectionSchema = z.object({ collectionId: idSchema('collection') })
export const RESERVED_SHOP_ALL_SLUG_MESSAGE = 'The URL handle "all" is reserved for Shop All.'
const COLLECTION_PRODUCTS_REQUIRED_MESSAGE = 'You must add at least one product to the collection'

const collectionFields = {
  name: z.string().min(1, 'Name is required'),
  slug: z
    .string()
    .min(1, 'Web address is required')
    .refine((slug) => slug.trim().toLowerCase() !== 'all', {
      message: RESERVED_SHOP_ALL_SLUG_MESSAGE,
    }),
  is_published: z.boolean().optional(),
  collection_origin: z.enum(['custom', 'platform']).optional(),
  collection_membership_type: z.enum(['manual', 'smart']).optional(),
  smart_rule: z
    .enum(['occasion_tags', 'designer_choice', 'subscription_packages'])
    .nullable()
    .optional(),
  smart_sort_mode: z.enum(['automatic', 'custom']).optional(),
  source_system: z.string().nullable().optional(),
  source_key: z.string().nullable().optional(),
  sync_occasion_product_membership: z.boolean().optional(),
  is_protected: z.boolean().optional(),
  media: z.array(z.object()).min(0).max(1, 'Only one image per collection is allowed').optional(),
  occasion_ids: z.array(idSchema('occasion')).optional(),
  season_ids: z.array(idSchema('season')).optional(),
}

export function collectionFormSchema() {
  return z.preprocess(
    (value) => {
      if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        !Object.hasOwn(value, 'is_published')
      ) {
        return {
          ...value,
          is_published: false,
        }
      }

      return value
    },
    z
      .object({
        ...collectionFields,
        products: z.array(z.object()),
        add_on_sets: z.array(z.object()).optional(),
      })
      .superRefine((value, context) => {
        const isSmartCollection = value.collection_membership_type === 'smart'
        const isSubscriptionCollection = isSubscriptionPackageCollection(value)

        if (
          value.is_published &&
          !isSmartCollection &&
          !isSubscriptionCollection &&
          value.products.length < 1
        ) {
          context.addIssue({
            code: 'custom',
            path: ['products'],
            message: COLLECTION_PRODUCTS_REQUIRED_MESSAGE,
          })
        }
      })
  )
}

export const collection = collectionFormSchema()
